// Phase I3 — Scoreline Probability Matrix.
// 12 most-probable scorelines · model% vs implied% · edge cells glow.
// Below: 5 derived market tiles (O/U, BTTS, AH, Draw, HT/FT).
// Empty-state copy when matrix is empty.

import { useState } from "react";
import { Eyebrow } from "../../../components/super-acca";
import type {
  DerivedMarketsView,
  ScorelineCell,
} from "../../../super-acca/view-models";

export interface ScorelineMatrixProps {
  matrix: ScorelineCell[];
  derived: DerivedMarketsView;
}

export function ScorelineMatrix({ matrix, derived }: ScorelineMatrixProps) {
  const [hover, setHover] = useState<string | null>(null);

  if (matrix.length === 0) {
    return (
      <div
        style={{
          marginTop: 28,
          border: "1px solid var(--sa-hairline)",
          background: "var(--sa-bg-panel)",
          borderRadius: 8,
          padding: 24,
        }}
      >
        <Eyebrow>Scoreline Probability Matrix</Eyebrow>
        <p
          style={{
            marginTop: 12,
            fontFamily: "var(--sa-font-body)",
            fontSize: 13.5,
            color: "var(--sa-fg-secondary)",
            lineHeight: 1.6,
          }}
        >
          Poisson matrix queued for next 11:45 UTC compute — the scoreline
          insight has not been published for this fixture yet.
        </p>
      </div>
    );
  }

  const max = Math.max(...matrix.map((c) => c.model));

  return (
    <div
      style={{
        marginTop: 28,
        border: "1px solid var(--sa-hairline)",
        background: "var(--sa-bg-panel)",
        borderRadius: 8,
      }}
    >
      <div
        style={{
          padding: "20px 24px",
          borderBottom: "1px solid var(--sa-hairline)",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 12,
        }}
      >
        <div>
          <Eyebrow>Scoreline Probability Matrix · Top {matrix.length}</Eyebrow>
          <div
            style={{
              fontFamily: "var(--sa-font-display)",
              fontSize: 22,
              fontWeight: 600,
              marginTop: 8,
              color: "var(--sa-fg-primary)",
            }}
          >
            One model · six markets
          </div>
        </div>
        <span
          style={{
            fontFamily: "var(--sa-font-mono)",
            fontSize: 11,
            color: "var(--sa-fg-muted)",
            letterSpacing: "0.16em",
          }}
        >
          POISSON · FORM-ADJUSTED xG
        </span>
      </div>
      <div style={{ padding: 20 }}>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(6, 1fr)",
            gap: 8,
          }}
        >
          {matrix.map((cell) => {
            const edgePos = cell.edge > 0;
            const strong = cell.edge >= 5;
            return (
              <div
                key={cell.score}
                onMouseEnter={() => setHover(cell.score)}
                onMouseLeave={() => setHover(null)}
                style={{
                  padding: 14,
                  background: strong
                    ? "rgba(212,168,67,0.10)"
                    : "var(--sa-bg-raised)",
                  border: `1px solid ${strong ? "var(--sa-amber-dim)" : "var(--sa-hairline)"}`,
                  borderRadius: 4,
                  position: "relative",
                  cursor: "pointer",
                  transition: "all .2s",
                  transform: hover === cell.score ? "translateY(-2px)" : "none",
                }}
              >
                {strong ? (
                  <div
                    style={{
                      position: "absolute",
                      top: 6,
                      right: 6,
                      fontFamily: "var(--sa-font-mono)",
                      fontSize: 8,
                      color: "var(--sa-accent-saffron)",
                      letterSpacing: "0.14em",
                    }}
                  >
                    EDGE
                  </div>
                ) : null}
                <div
                  style={{
                    fontFamily: "var(--sa-font-stat)",
                    fontSize: 30,
                    lineHeight: 1,
                    color: "var(--sa-fg-primary)",
                  }}
                >
                  {cell.score}
                </div>
                <div
                  style={{
                    marginTop: 10,
                    height: 4,
                    background: "var(--sa-hairline-bright)",
                    borderRadius: 1,
                    overflow: "hidden",
                  }}
                >
                  <div
                    style={{
                      height: "100%",
                      width: `${(cell.model / max) * 100}%`,
                      background: edgePos
                        ? "var(--sa-accent-saffron)"
                        : "var(--sa-fg-muted)",
                    }}
                  />
                </div>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    marginTop: 8,
                    fontFamily: "var(--sa-font-mono)",
                    fontSize: 10,
                  }}
                >
                  <span style={{ color: "var(--sa-fg-primary)" }}>{cell.model}%</span>
                  <span style={{ color: "var(--sa-fg-muted)" }}>{cell.implied}%</span>
                </div>
                <div
                  style={{
                    fontFamily: "var(--sa-font-mono)",
                    fontSize: 10,
                    marginTop: 4,
                    color: edgePos ? "var(--sa-signal-green)" : "var(--sa-signal-red)",
                  }}
                >
                  {edgePos ? "+" : ""}
                  {cell.edge}pp
                </div>
              </div>
            );
          })}
        </div>

        {/* Derived markets */}
        <div
          style={{
            marginTop: 22,
            padding: 16,
            background: "var(--sa-bg-base)",
            border: "1px solid var(--sa-hairline)",
            borderRadius: 4,
          }}
        >
          <Eyebrow>Derived Markets · same matrix feeds five</Eyebrow>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(5, 1fr)",
              gap: 14,
              marginTop: 12,
            }}
          >
            <Derived
              label={`O/U ${derived.over_under?.line ?? 2.5}`}
              model={derived.over_under ? `${derived.over_under.true_prob}%` : "—"}
              edge={derived.over_under?.edge ?? null}
            />
            <Derived
              label="BTTS Yes"
              model={derived.btts ? `${derived.btts.true_prob}%` : "—"}
              edge={derived.btts?.edge ?? null}
            />
            <Derived
              label={`AH ${derived.ah?.line ?? "—"}`}
              model={derived.ah ? `${derived.ah.true_prob}%` : "—"}
              edge={derived.ah?.edge ?? null}
            />
            <Derived
              label="Draw"
              model={derived.draw ? `${derived.draw.true_prob}%` : "—"}
              edge={derived.draw?.edge ?? null}
            />
            <Derived
              label="HT/FT"
              model={derived.htft?.recommended ?? "—"}
              edge={null}
              sub={derived.htft ? `${derived.htft.model_prob}%` : null}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

function Derived({
  label,
  model,
  edge,
  sub,
}: {
  label: string;
  model: string;
  edge: number | null;
  sub?: string | null;
}) {
  return (
    <div>
      <div
        style={{
          fontFamily: "var(--sa-font-mono)",
          fontSize: 9.5,
          color: "var(--sa-fg-muted)",
          letterSpacing: "0.18em",
        }}
      >
        {label.toUpperCase()}
      </div>
      <div
        style={{
          fontFamily: "var(--sa-font-stat)",
          fontSize: 28,
          lineHeight: 1,
          color: "var(--sa-fg-primary)",
          marginTop: 6,
        }}
      >
        {model}
      </div>
      {edge !== null ? (
        <div
          style={{
            fontFamily: "var(--sa-font-mono)",
            fontSize: 10,
            color: edge > 0 ? "var(--sa-signal-green)" : "var(--sa-signal-red)",
            marginTop: 4,
          }}
        >
          {edge > 0 ? "+" : ""}
          {edge}pp edge
        </div>
      ) : null}
      {sub ? (
        <div
          style={{
            fontFamily: "var(--sa-font-mono)",
            fontSize: 10,
            color: "var(--sa-fg-muted)",
            marginTop: 4,
          }}
        >
          {sub}
        </div>
      ) : null}
    </div>
  );
}
